/**
 * What a project's menu does, and the dialogs it opens. Archive happens at
 * once with a five second Undo (the API restores a project, so Undo sends the
 * restore). Delete is permanent, so it asks first and says what goes. A delete
 * the API refuses because others worked in the project offers Archive instead.
 */
import { useState, type ReactNode } from "react";
import { isAxiosError } from "axios";
import { Archive } from "lucide-react";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { WorkshopProject } from "@/types/workshop.type";
import { PROJECT_HAS_COLLABORATORS, useWorkshopProjects } from "../api/useWorkshopProjects";
import ProjectMembersModal from "./ProjectMembersModal";
import WorkshopDialog from "./WorkshopDialog";
import WorkshopProjectDialog from "./WorkshopProjectDialog";
import { useWorkshopUndo } from "./WorkshopUndo";
import type { ProjectMenuAction } from "./WorkshopProjectMenu";

/** “Name” goes with its chats, outputs and files. Nobody else worked in it. */
export function deleteProjectLine(project: WorkshopProject): string {
  return `“${project.name}” goes with its chats, outputs and files. Nobody else worked in it.`;
}

type Options = {
  brandId: string;
  onProjectDeleted?: (projectId: string) => void;
};

export function useProjectActions({ brandId, onProjectDeleted }: Options) {
  const { updateProject, deleteProject } = useWorkshopProjects(brandId);
  const undo = useWorkshopUndo();
  const [editing, setEditing] = useState<WorkshopProject | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WorkshopProject | null>(null);
  const [archiveInstead, setArchiveInstead] = useState<WorkshopProject | null>(null);
  const [peopleFor, setPeopleFor] = useState<WorkshopProject | null>(null);

  const mutationError = (title: string) => (error: unknown) =>
    queue.add({ title, description: getAiRequestErrorMessage(error), variant: "error" });

  const setStatus = (project: WorkshopProject, status: "active" | "archived", failTitle: string) =>
    updateProject.mutate(
      { projectId: project.id, status },
      { onError: mutationError(failTitle) },
    );

  const archive = (project: WorkshopProject) => {
    const key = undo.show({
      icon: Archive,
      title: `“${project.name}” archived`,
      line: "It left Projects and the sidebar. Its chats and outputs stay.",
      undo: () => setStatus(project, "active", "Couldn't restore the project"),
    });
    // A failed archive takes its Undo down with it: there is nothing to undo.
    // mutateAsync, not mutate's callbacks, so this holds after the home unmounts.
    updateProject.mutateAsync({ projectId: project.id, status: "archived" }).catch((error) => {
      undo.dismiss(key);
      mutationError("Couldn't archive the project")(error);
    });
  };

  const run = (action: ProjectMenuAction, project: WorkshopProject) => {
    switch (action) {
      case "edit":
        setEditing(project);
        return;
      case "share":
      case "people":
        setPeopleFor(project);
        return;
      case "archive":
        archive(project);
        return;
      case "restore":
        setStatus(project, "active", "Couldn't restore the project");
        return;
      case "delete":
        setPendingDelete(project);
        return;
    }
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    const project = pendingDelete;
    setPendingDelete(null);
    onProjectDeleted?.(project.id);
    // Gone from the list at once; it comes back if the delete is refused.
    deleteProject.mutate(project.id, {
      onError: (error) => {
        if (
          isAxiosError(error) &&
          error.response?.status === 409 &&
          (error.response.data as { code?: string } | undefined)?.code ===
            PROJECT_HAS_COLLABORATORS
        ) {
          setArchiveInstead(project);
          return;
        }
        mutationError("Couldn't delete the project")(error);
      },
    });
  };

  const dialogs: ReactNode = (
    <>
      <WorkshopProjectDialog
        brandId={brandId}
        isOpen={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        project={editing}
      />

      <WorkshopDialog
        isOpen={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this project?"
        description={
          pendingDelete ? deleteProjectLine(pendingDelete) : undefined
        }
        consequence="This cannot be undone"
        cancelLabel="Keep project"
        confirmLabel="Delete project"
        confirmTone="danger"
        onConfirm={handleDelete}
      />

      <WorkshopDialog
        isOpen={archiveInstead !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveInstead(null);
        }}
        title={archiveInstead ? `${archiveInstead.name} can’t be deleted`.toUpperCase() : ""}
        description="Others worked in it, so it stays. Archive it instead: it leaves Projects and the sidebar, and everything in it is kept."
        cancelLabel="Keep it"
        confirmLabel="Archive"
        onConfirm={() => {
          if (!archiveInstead) return;
          const project = archiveInstead;
          setArchiveInstead(null);
          archive(project);
        }}
      />

      {peopleFor ? (
        <ProjectMembersModal
          projectId={peopleFor.id}
          projectName={peopleFor.name}
          isOwner={peopleFor.isOwner !== false}
          isOpen={peopleFor !== null}
          onOpenChange={(open) => {
            if (!open) setPeopleFor(null);
          }}
        />
      ) : null}
    </>
  );

  return { run, dialogs };
}
